import { AppIcon } from '@/components/ui/app-icon';
import { AuthLayout } from '@/components/auth/auth-layout.component';
import { AuthPreviewCarousel } from '@/components/auth/auth-preview-carousel.component';
import { DesktopOnlyNotice } from '@/components/auth/desktop-only-notice.component';
import { useAuthRedirect } from '@/hooks/use-auth-redirect.hook';
import { peekPostAuthRedirect } from '@/lib/post-auth-redirect';
import { useSignupsPaused } from '@/hooks/use-signups-paused.hook';
import { useTheme } from '@/hooks/use-theme.hook';
import {
  CARD_TRIAL_DAYS,
  PRO_TRIAL_CREDITS,
  WORK_EMAIL_BONUS_CREDITS,
} from '@/lib/card-trial';
import { iconGift } from '@/lib/icons';
import { SignUp } from '@clerk/react';
import { createFileRoute } from '@tanstack/react-router';
import '../../../src/index.css';
import { BRAND, siteUrl } from '@/lib/brand';

export const Route = createFileRoute('/auth/register')({
  component: SignUpPage,
});

function SignUpPage() {
  useAuthRedirect();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const signupsPaused = useSignupsPaused();

  if (signupsPaused) {
    return (
      <AuthLayout
        leftPanel={
          <div className='text-center'>
            <h1 className='text-xl font-semibold text-foreground'>
              Sign-ups are paused
            </h1>
            <p className='mt-2 text-sm text-muted-foreground'>
              We&apos;re not accepting new accounts right now. Please check back
              soon, or reach us at{' '}
              <a
                href='mailto:support@cleanlist.ai'
                className='font-medium text-primary hover:underline'
              >
                support@cleanlist.ai
              </a>
              .
            </p>
          </div>
        }
        rightPanel={<AuthPreviewCarousel variant='register' />}
      />
    );
  }

  return (
    <AuthLayout
      leftPanel={
        <>
          <DesktopOnlyNotice />
          <div className='mb-4 text-center'>
            <h1 className='text-xl font-semibold text-foreground'>
              Become a {BRAND.dotName} member
            </h1>
            <p className='mt-1 text-sm text-tertiary-foreground'>
              {CARD_TRIAL_DAYS}-day Pro trial with {PRO_TRIAL_CREDITS} credits
            </p>
            <p className='mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary/[0.07] px-3 py-1 text-[13px] font-medium text-primary'>
              <AppIcon icon={iconGift} className='h-3 w-3' />
              Register with your work email for {WORK_EMAIL_BONUS_CREDITS} extra
              credits
            </p>
          </div>

          <SignUp
            fallbackRedirectUrl={peekPostAuthRedirect() ?? '/'}
            signInUrl='/auth/login'
            appearance={{
              elements: {
                // Full width, never wider: Clerk's card is wider than a
                // phone's column, and without these it spills off the right.
                rootBox: 'mx-auto w-full max-w-full',
                cardBox:
                  'bg-white dark:bg-[#1a1a1a] shadow-xs dark:shadow-none border border-gray-100 dark:border-white/6 w-full max-w-full',
                card: 'bg-white dark:bg-[#1a1a1a] shadow-none border-0 w-full max-w-full',
                headerTitle: 'text-gray-900 dark:text-white',
                headerSubtitle: 'text-gray-500 dark:text-gray-400',
                socialButtonsBlockButton:
                  'rounded-full! bg-white border-gray-100 hover:bg-gray-50 text-gray-700 dark:bg-[#1a1a1a] dark:border-white/6 dark:hover:bg-white/[0.03] dark:text-gray-300',
                formFieldLabel: 'text-gray-700 dark:text-gray-300',
                formFieldInput:
                  'bg-white border-gray-100 text-gray-900 placeholder:text-gray-400 dark:bg-[#1a1a1a] dark:border-white/6 dark:text-white dark:placeholder:text-gray-500',
                // Flat pill CTA to match the redesign: bg-primary keeps the
                // solid Lemlist fill, but !bg-none/!shadow-none suppress the
                // global button.bg-primary 3D gradient+glow (index.css) so it
                // reads flat, and rounded-full! makes it a pill.
                formButtonPrimary:
                  'bg-primary text-primary-foreground rounded-full! bg-none! shadow-none! font-semibold',
                footer:
                  'bg-white text-gray-500 dark:bg-[#1a1a1a] dark:text-gray-400',
                footerAction: 'bg-white dark:bg-[#1a1a1a]',
                footerActionLink: 'text-primary hover:text-primary/80',
                footerActionText: 'text-gray-500 dark:text-gray-400',
                footerPages: 'bg-white dark:bg-[#1a1a1a]',
                footerPageLink:
                  'bg-white text-gray-500 dark:bg-[#1a1a1a] dark:text-gray-400',
                identityPreviewText: 'text-gray-700 dark:text-gray-300',
                identityPreviewEditButtonIcon:
                  'text-gray-500 dark:text-gray-400',
                formFieldInputShowPasswordButton:
                  'text-gray-500 dark:text-gray-400',
                otpCodeFieldInput:
                  'bg-white border-gray-100 text-gray-900 dark:bg-[#1a1a1a] dark:border-white/6 dark:text-white',
                formResendCodeLink: 'text-primary hover:text-primary/80',
                logoBox:
                  'bg-white text-gray-500 dark:bg-[#1a1a1a] dark:text-gray-400',
                logoImage: 'brightness-100 dark:brightness-90',
              },
              variables: {
                // Hex equivalent of HSL(222, 100%, 60%) - the current
                // --primary token in index.css. Clerk's variables don't
                // accept HSL/CSS-vars, so we mirror the token's hex value
                // here and update if --primary shifts.
                colorPrimary: '#316bff',
                colorDanger: '#ef4444',
                colorSuccess: '#10b981',
                colorWarning: '#f59e0b',
                colorNeutral: '#6b7280',
                colorBackground: isDark ? '#1a1a1a' : '#ffffff',
                colorForeground: isDark ? '#ffffff' : '#111827',
                colorMutedForeground: isDark ? '#9ca3af' : '#6b7280',
              },
            }}
          />

          {/* Clerk reports these one rejection at a time (Replay: sign-ups
              retrying Continue through "too short", "found in a data breach"
              and "contains your email"). These are the production instance's
              rules: Clerk's 8-character minimum and breached-password check. */}
          <p className='mx-auto mt-3 max-w-sm text-center text-xs text-muted-foreground'>
            Passwords need at least 8 characters, can&apos;t include your email,
            and can&apos;t be one that has appeared in a data breach.
          </p>

          <p className='mt-3 text-center text-xs text-tertiary-foreground'>
            By signing up for a {BRAND.name} account, you agree to our{' '}
            <a
              href={siteUrl('https://www.cleanlist.ai/privacy-policy')}
              target='_blank'
              rel='noopener noreferrer'
              className='font-semibold text-foreground hover:text-primary'
            >
              Privacy Policy
            </a>{' '}
            and{' '}
            <a
              href={siteUrl('https://www.cleanlist.ai/terms-conditions')}
              target='_blank'
              rel='noopener noreferrer'
              className='font-semibold text-foreground hover:text-primary'
            >
              Terms of Service
            </a>
          </p>
        </>
      }
      rightPanel={<AuthPreviewCarousel variant='register' />}
    />
  );
}

export default SignUpPage;
